import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, TrendingUp, Users, DollarSign, Film, Building2, MapPin, Calendar, Download, Filter, RefreshCw, FileSpreadsheet, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple Bar Chart Component
const SimpleBarChart = ({ data, valueKey, labelKey, color = '#e4002b', maxBars = 10 }) => {
  const displayData = data.slice(0, maxBars);
  const maxValue = Math.max(...displayData.map(d => d[valueKey] || 0), 1);
  
  return (
    <div className="space-y-2">
      {displayData.map((item, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <div className="w-24 text-white/70 text-xs truncate" title={item[labelKey]}>
            {item[labelKey]}
          </div>
          <div className="flex-1 h-6 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(item[valueKey] / maxValue) * 100}%`,
                backgroundColor: color
              }}
            />
          </div>
          <div className="w-16 text-right text-white font-medium text-sm">
            {item[valueKey]?.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

// Pie Chart Component
const SimplePieChart = ({ data, valueKey, labelKey, colors }) => {
  const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
  let cumulativePercent = 0;
  
  const defaultColors = ['#e4002b', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
  const chartColors = colors || defaultColors;
  
  const segments = data.map((item, idx) => {
    const percent = total > 0 ? (item[valueKey] / total) * 100 : 0;
    const startPercent = cumulativePercent;
    cumulativePercent += percent;
    return {
      ...item,
      percent,
      startPercent,
      color: chartColors[idx % chartColors.length]
    };
  });
  
  // Create conic gradient
  const gradientStops = segments.map(s => 
    `${s.color} ${s.startPercent}% ${s.startPercent + s.percent}%`
  ).join(', ');
  
  return (
    <div className="flex items-center gap-6">
      <div 
        className="w-32 h-32 rounded-full"
        style={{ 
          background: total > 0 
            ? `conic-gradient(${gradientStops})` 
            : '#333'
        }}
      />
      <div className="flex-1 space-y-1">
        {segments.slice(0, 6).map((s, idx) => (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: s.color }} />
            <span className="text-white/70 truncate flex-1">{s[labelKey]}</span>
            <span className="text-white font-medium">{s.percent.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Line Chart Component (for monthly data)
const SimpleLineChart = ({ data, valueKey, labelKey }) => {
  const maxValue = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const minValue = 0;
  const range = maxValue - minValue;
  
  if (data.length === 0) return <div className="h-48 flex items-center justify-center text-white/50">Sin datos</div>;
  
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1 || 1)) * 100,
    y: 100 - ((d[valueKey] - minValue) / (range || 1)) * 100,
    value: d[valueKey],
    label: d[labelKey]
  }));
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  return (
    <div className="relative h-48">
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(y => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
        ))}
        {/* Line */}
        <path d={pathD} fill="none" stroke="#e4002b" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {/* Area */}
        <path 
          d={`${pathD} L 100 100 L 0 100 Z`} 
          fill="url(#areaGradient)" 
          opacity="0.3" 
        />
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e4002b" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
        {/* Points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="1.5" fill="#e4002b" />
        ))}
      </svg>
      {/* Labels */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-white/50 mt-2">
        {data.filter((_, i) => i % Math.ceil(data.length / 6) === 0 || i === data.length - 1).map((d, i) => (
          <span key={i}>{d[labelKey]?.slice(5)}</span>
        ))}
      </div>
    </div>
  );
};

// KPI Card
const KPICard = ({ icon: Icon, label, value, subvalue, color = '#e4002b' }) => (
  <div className="bg-white/5 rounded-xl p-5 border border-white/10">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white/60 text-sm">{label}</p>
        <p className="text-white text-3xl font-bold mt-1">{value}</p>
        {subvalue && <p className="text-white/50 text-sm mt-1">{subvalue}</p>}
      </div>
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </div>
);

const ScreeningsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCinema, setSelectedCinema] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedYear) params.append('year', selectedYear);
      if (selectedMonth) params.append('month', selectedMonth);
      if (selectedCinema) params.append('cinema_id', selectedCinema);
      if (selectedProvider) params.append('provider', selectedProvider);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const res = await fetch(`${BACKEND_URL}/api/dashboard/screenings?${params}`);
      if (!res.ok) throw new Error('Error loading data');
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, selectedCinema, selectedProvider, selectedCategory]);

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (selectedYear) params.append('year', selectedYear);
      if (selectedMonth) params.append('month', selectedMonth);
      if (selectedCinema) params.append('cinema_id', selectedCinema);
      if (selectedProvider) params.append('provider', selectedProvider);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const res = await fetch(`${BACKEND_URL}/api/dashboard/screenings/export?${params}`);
      
      if (format === 'csv') {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pases_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const jsonData = await res.json();
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pases_export_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert('Error al exportar: ' + err.message);
    }
  };

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  }, []);

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white/50">Cargando dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-[#e4002b]" />
            Dashboard de Pases
          </h1>
          <p className="text-white/50 text-sm mt-1">Análisis de pases aprobados y realizados</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          <button
            onClick={() => handleExport('csv')}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel (CSV)
          </button>
          <button
            onClick={() => handleExport('json')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FileText className="w-4 h-4" />
            JSON
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-white/60" />
          <span className="text-white/60 text-sm font-medium">Filtros</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          >
            <option value="">Todos los años</option>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          >
            <option value="">Todos los meses</option>
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          >
            <option value="">Todas las categorías</option>
            {data?.filters?.categories?.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          >
            <option value="">Todos los proveedores</option>
            {data?.filters?.providers?.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          
          <select
            value={selectedCinema}
            onChange={(e) => setSelectedCinema(e.target.value)}
            className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          >
            <option value="">Todos los cines</option>
            {data?.filters?.cinemas?.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          icon={Film}
          label="Total Pases"
          value={data?.summary?.total_screenings?.toLocaleString() || 0}
          color="#e4002b"
        />
        <KPICard
          icon={Users}
          label="Total Espectadores"
          value={data?.summary?.total_spectators?.toLocaleString() || 0}
          color="#3b82f6"
        />
        <KPICard
          icon={DollarSign}
          label="Recaudación Total"
          value={`${(data?.summary?.total_revenue || 0).toLocaleString()}€`}
          color="#22c55e"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Category (Pie) */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Film className="w-4 h-4 text-[#e4002b]" />
            Pases por Género
          </h3>
          <SimplePieChart 
            data={data?.by_category || []} 
            valueKey="count" 
            labelKey="category" 
          />
        </div>

        {/* By Region (Bar) */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#e4002b]" />
            Pases por Comunidad Autónoma
          </h3>
          <SimpleBarChart 
            data={data?.by_region || []} 
            valueKey="count" 
            labelKey="region"
            color="#3b82f6"
          />
        </div>

        {/* By Month (Line) */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 lg:col-span-2">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#e4002b]" />
            Evolución Mensual
          </h3>
          <SimpleLineChart 
            data={data?.by_month || []} 
            valueKey="count" 
            labelKey="month" 
          />
        </div>

        {/* By Title (Bar) */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#e4002b]" />
            Top Títulos
          </h3>
          <SimpleBarChart 
            data={data?.by_title || []} 
            valueKey="count" 
            labelKey="title"
            color="#22c55e"
          />
        </div>

        {/* By Cinema (Bar) */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#e4002b]" />
            Top Cines
          </h3>
          <SimpleBarChart 
            data={data?.by_cinema || []} 
            valueKey="count" 
            labelKey="cinema"
            color="#f59e0b"
          />
        </div>

        {/* By Provider (Bar) */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 lg:col-span-2">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#e4002b]" />
            Pases por Proveedor
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-white/50 text-sm mb-3">Número de Pases</p>
              <SimpleBarChart 
                data={data?.by_provider || []} 
                valueKey="count" 
                labelKey="provider"
                color="#8b5cf6"
              />
            </div>
            <div>
              <p className="text-white/50 text-sm mb-3">Recaudación (€)</p>
              <SimpleBarChart 
                data={data?.by_provider || []} 
                valueKey="revenue" 
                labelKey="provider"
                color="#ec4899"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white/5 rounded-xl p-5 border border-white/10">
        <h3 className="text-white font-semibold mb-4">Detalle de Pases</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/60 font-medium">Fecha</th>
                <th className="text-left py-3 px-4 text-white/60 font-medium">Título</th>
                <th className="text-left py-3 px-4 text-white/60 font-medium">Categoría</th>
                <th className="text-left py-3 px-4 text-white/60 font-medium">Cine</th>
                <th className="text-left py-3 px-4 text-white/60 font-medium">Región</th>
                <th className="text-right py-3 px-4 text-white/60 font-medium">Espectadores</th>
                <th className="text-right py-3 px-4 text-white/60 font-medium">Recaudación</th>
                <th className="text-center py-3 px-4 text-white/60 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {(data?.screenings || []).slice(0, 20).map((s, idx) => (
                <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 px-4 text-white">{s.screening_date}</td>
                  <td className="py-3 px-4 text-white truncate max-w-[200px]" title={s.content_title}>
                    {s.content_title}
                  </td>
                  <td className="py-3 px-4 text-white/70">{s.content_category}</td>
                  <td className="py-3 px-4 text-white/70 truncate max-w-[150px]" title={s.cinema_name}>
                    {s.cinema_name}
                  </td>
                  <td className="py-3 px-4 text-white/70">{s.cinema_region}</td>
                  <td className="py-3 px-4 text-right text-white">{s.spectators?.toLocaleString() || '-'}</td>
                  <td className="py-3 px-4 text-right text-white">{s.revenue ? `${s.revenue.toLocaleString()}€` : '-'}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      s.report_status === 'reported' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {s.report_status === 'reported' ? 'Reportado' : 'Pendiente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data?.screenings?.length || 0) > 20 && (
            <p className="text-white/50 text-sm text-center py-3">
              Mostrando 20 de {data.screenings.length} pases. Exporta para ver todos.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreeningsDashboard;
